import { changeable } from '../../lib/proformaState.ts';

type MoneyFacts = { status: string; paidAmount?: number | string | null };

export function changeableBeforePayment(
  status: string | undefined,
  paidAmount: number | string | null | undefined,
): boolean {
  return changeable(status, paidAmount);
}

const carriesMoney = (i: MoneyFacts) =>
  i.status === 'paid' || Math.round(Number(i.paidAmount ?? 0) * 100) > 0;

export type CancelOutcome =
  | 'awaits-new-proforma'
  | 'offer-stays'
  | 'returns-first-installment'
  | 'returns-to-schedule'
  | 'cancels-renewal'
  | 'offer-untouched';

type CancelTarget = {
  id: number;
  offerId: number | null;
  renewalId: number | null;
  partNumber?: number | null;
};

const otherLive = <T extends { id: number; status: string }>(
  invoice: CancelTarget,
  offerInvoices: T[],
) =>
  offerInvoices.filter((i) => i.id !== invoice.id && i.status !== 'cancelled');

export function cancelOutcome(
  invoice: CancelTarget,
  offerInvoices: { id: number; status: string }[],
): CancelOutcome {
  if (invoice.renewalId != null) return 'cancels-renewal';
  if (invoice.offerId == null) return 'offer-untouched';
  if (invoice.partNumber != null) {
    return invoice.partNumber <= 1
      ? 'returns-first-installment'
      : 'returns-to-schedule';
  }
  return otherLive(invoice, offerInvoices).length === 0
    ? 'awaits-new-proforma'
    : 'offer-stays';
}

export function moneyOnOtherProformas(
  invoice: CancelTarget,
  offerInvoices: ({ id: number } & MoneyFacts)[],
): boolean {
  return otherLive(invoice, offerInvoices).some(carriesMoney);
}

export function unlockAllowed(offerInvoices: MoneyFacts[]): boolean {
  return offerInvoices.every(
    (i) =>
      i.status === 'cancelled' ||
      changeableBeforePayment(i.status, i.paidAmount),
  );
}

export function billToRefreshOffered(args: {
  status: string | undefined;
  paidAmount: number | string | null | undefined;
  mayWrite: boolean;
  refreshed: boolean | undefined;
}): boolean {
  return (
    args.mayWrite &&
    changeableBeforePayment(args.status, args.paidAmount) &&
    !!args.refreshed
  );
}

export interface ReissueFacts {
  available: boolean;
  currentInvoice: { id: number } | null;
}

export function reissueAction(
  reissue: ReissueFacts | null | undefined,
  user: { mayWrite: boolean; mayIssue: boolean },
): 'issue' | 'replaced' | 'locked' | null {
  if (!reissue) return null;
  if (reissue.currentInvoice) return 'replaced';
  if (!user.mayWrite && !user.mayIssue) return null;
  return reissue.available ? 'issue' : 'locked';
}
